import { http } from '@/core/api';
import { executeRequest } from '@/core/either';
import type { ApiResult } from '@/core/either';
import type { DocumentType } from '@/types/DocumentType';
import type {
  CompanyDocumentsByType,
  InvitationFilesResponse,
} from '@/shared/common/types/companyDocuments.type';
import type { AxiosProgressEvent } from 'axios';

export const documentsService = {
  /**
   * Obtiene los tipos de documento por tipo y ruta.
   * @param type - Tipo de documento.
   * @param routeId - ID de la ruta.
   * @returns Promesa con el resultado tipado usando Either (ApiResult<DocumentType[]>).
   */
  getDocumentTypesByRoute(
    type: string,
    routeId: number
  ): Promise<ApiResult<DocumentType[]>> {
    return executeRequest(() =>
      http.get<DocumentType[]>(
        `route/b2b/documents?idClass=${type}&routeId=${routeId}`
      )
    );
  },

  /**
   * Obtiene los tipos de documento por código de país.
   * @param countryCode - Código alpha2 del país.
   * @param entityType - Tipo de entidad opcional.
   * @returns Promesa con el resultado tipado usando Either (ApiResult<DocumentType[]>).
   */
  getDocumentTypesByCountry(
    countryCode: string,
    entityType?: string | null
  ): Promise<ApiResult<DocumentType[]>> {
    const typeDocument = entityType ? `?entityType=${entityType}` : '';
    return executeRequest(() =>
      http.get<DocumentType[]>(
        `route/b2b/documents/${countryCode}${typeDocument}`
      )
    );
  },
  /**
   * Obtiene los documentos cargados por la empresa, agrupados por tipo de documento.
   * @returns Promesa con el resultado tipado usando Either; `data` es un mapa tipo de documento → documentos.
   */
  getCompanyDocuments(): Promise<ApiResult<CompanyDocumentsByType>> {
    return executeRequest(() =>
      http.get<CompanyDocumentsByType>('company/b2b/documents')
    );
  },

  /**
   * Sube archivos/documentos de la empresa.
   * Antes: companyDocumentsService.postCompanyFiles()
   * @param formData - FormData con los archivos a subir.
   * @param onUploadProgress - Callback opcional para reportar progreso de subida.
   * @returns Promesa con el resultado tipado usando Either (ApiResult<void>).
   */
  postCompanyFiles(
    formData: FormData,
    onUploadProgress?: (progressEvent: AxiosProgressEvent) => void
  ): Promise<ApiResult<void>> {
    return executeRequest(() =>
      http.post<void>('company/b2b/documents', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress,
      })
    );
  },

  /**
   * Obtiene los archivos del mini onboarding ya subidos (invitaciones).
   * Antes: onboardingDocumentsService.getFilesMiniOnboarding()
   * @returns Promesa con el resultado tipado usando Either (ApiResult<InvitationFilesResponse>).
   */
  getInvitationFiles(): Promise<ApiResult<InvitationFilesResponse>> {
    return executeRequest(() =>
      http.get<InvitationFilesResponse>('company/b2b/onboarding/documents/invitations')
    );
  },
};
